import { ReactElement, useEffect, useState } from 'react'
import { AsyncThunkAction, AsyncThunkConfig } from '@reduxjs/toolkit'
import { useForm } from '@tanstack/react-form'
import { sort, uniq } from 'ramda'

import axios, { pluckError } from 'src/utilities/http-client'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchRecertificationCycleCategories,
  fetchRecertificationCycleTemplates,
  selectRecertificationCycleTemplates,
  selectRecertificationCycleTemplatesStatus,
} from 'src/v3/profile/profile.slice'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { hasNoTypes, templateTypeLabels } from 'src/v3/utilities/template-types'

type TemplateFormProps = {
  cycleID: string
  refetchCycles: AsyncThunkAction<any, any, AsyncThunkConfig>
  open: boolean
  setOpen: (status: boolean) => void
}

const UNGROUPED = 'Ungrouped'

export let TemplateForm = ({
  cycleID,
  refetchCycles,
  open,
  setOpen,
}: TemplateFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()

  let templates =
    useAppSelector(selectRecertificationCycleTemplates(cycleID)) ?? []
  let templatesStatus = useAppSelector(
    selectRecertificationCycleTemplatesStatus(cycleID),
  )

  let [saving, setSaving] = useState(false)

  useEffect(() => {
    if (templatesStatus === 'uninitialized')
      dispatch(fetchRecertificationCycleTemplates(cycleID))
  }, [cycleID, templatesStatus])

  let form = useForm({
    defaultValues: { template: 'unselected' },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      axios
        .post(`/api/profile/recertification-cycle/apply-template`, {
          cycleID,
          templateID: value.template,
        })
        .then(() => {
          dispatch(fetchRecertificationCycleCategories(cycleID))
          dispatch(refetchCycles)
          setOpen(false)
          formApi.reset()
        })
        .catch((err) => {
          showError?.(
            pluckError(err, 'Error Applying Recertification Cycle Template'),
          )
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 450, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Apply Cycle Template
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <form.Field
              name="template"
              validators={{
                onSubmit: ({ value }) =>
                  value === 'unselected'
                    ? 'Template is a required field'
                    : undefined,
              }}
              children={(subField) => (
                <FormControl
                  fullWidth
                  error={subField.state.meta.errors.length > 0}
                >
                  <InputLabel id="template-label">Template</InputLabel>
                  <Select
                    labelId="template-label"
                    value={subField.state.value}
                    label="Template"
                    onChange={(e) => subField.handleChange(e.target.value)}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    <MenuItem value="unselected">Unselected</MenuItem>
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {subField.state.meta.errors.length > 0 && (
                    <FormHelperText>
                      {subField.state.meta.errors.join(', ')}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <form.Subscribe
              selector={(state) => state.values.template}
              children={(t) => {
                let template = templates.find((tem) => tem.id === t)

                if (!template) return <Box></Box>

                let groups = sort(
                  (a, b) => a.localeCompare(b),
                  uniq(
                    template.categories.map((c) => c.subcategory || UNGROUPED),
                  ),
                )

                return (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    <Typography color="text.secondary" sx={{ ml: 1 }}>
                      <strong>Applies to:</strong>{' '}
                      {hasNoTypes(template.types)
                        ? 'Any license or certification'
                        : templateTypeLabels(template.types).join(' · ')}
                    </Typography>
                    {template.description && (
                      <Typography
                        color="textSecondary"
                        sx={{ ml: 1, whiteSpace: 'pre-line' }}
                      >
                        {template.description}
                      </Typography>
                    )}
                    {groups.map((group) => {
                      let categories = template.categories.filter(
                        (c) => (c.subcategory || UNGROUPED) === group,
                      )
                      return (
                        <Box key={group} sx={{ mt: 0.5 }}>
                          <Typography
                            color="text.secondary"
                            sx={{
                              ml: 1,
                              fontWeight: 'bold',
                            }}
                          >
                            {group}
                          </Typography>
                          {categories.map((category) => (
                            <Box key={category.id}>
                              {'\u2022'} {category.hours} Hour
                              {category.hours === 1 ? '' : 's'} {'\u2014'}{' '}
                              {category.name}
                            </Box>
                          ))}
                        </Box>
                      )
                    })}
                  </Box>
                )
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              onClick={handleOnClose}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
      </Paper>
    </Dialog>
  )
}
